import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, Clock, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import { STORE, IMAGES, BRAND, saveBooking, waLink } from "../lib/store";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { RegalClosing } from "../components/RegalClosing";
import { trackAppointment } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const TIME_SLOTS = [
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
  "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM",
];

const INTERESTS = [
  "22K Gold Jewelry", "Diamond Jewelry", "Bridal / Wedding Sets",
  "Wedding Bands (MALO)", "Gold & Silver Bullion", "Silver Jewelry", "Custom Order", "Other",
];

export default function BookMeeting() {
  const [params] = useSearchParams();
  const productInterest = params.get("product");
  const [date, setDate] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", time: "", interest: productInterest ? "Specific piece" : "", notes: productInterest ? `Interested in: ${productInterest}` : "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Please add your name and phone number");
    if (!date) return toast.error("Please select a date");
    if (!form.time) return toast.error("Please select a time slot");
    const booking = { ...form, date: date.toDateString() };
    try {
      await saveBooking(booking);
      trackAppointment();
      setConfirmed(booking);
      toast.success("Appointment request received!");
    } catch (err) {
      toast.error("Could not submit request. Please try again or WhatsApp us.");
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-24 text-center">
        <CheckCircle2 size={56} strokeWidth={1.2} className="text-[#3f6b3a] mx-auto" />
        <h1 className="font-heading text-3xl sm:text-4xl text-[#171310] mt-6">Appointment Requested</h1>
        <p className="text-[#5c5240] mt-4 leading-relaxed">
          Thank you, {confirmed.name}! We've received your request for{" "}
          <span className="text-[#171310] font-medium">{confirmed.date} at {confirmed.time}</span>.
          Our team will confirm shortly by phone or WhatsApp.
        </p>
        <div className="bg-[#f4eddc] border border-[#e8e0d0] p-6 mt-8 text-left text-sm text-[#5c5240] space-y-2">
          <p className="flex gap-2"><MapPin size={16} className="text-[#a8842c] shrink-0 mt-0.5" /> {STORE.address}</p>
          <p className="flex gap-2"><Clock size={16} className="text-[#a8842c] shrink-0 mt-0.5" /> {STORE.hours.map((h) => `${h.d}: ${h.h}`).join(" • ")}</p>
        </div>
        <a href={waLink(`Hello Regal Jewels! I just requested an appointment for ${confirmed.date} at ${confirmed.time} under the name ${confirmed.name}.`)} target="_blank" rel="noopener noreferrer">
          <Button className="mt-8 rounded-none bg-[#25D366] hover:bg-[#1fb457] text-white tracking-[0.12em] uppercase text-xs h-12 px-8 transition-colors duration-300">
            <MessageCircle size={15} className="mr-2" /> Confirm Faster on WhatsApp
          </Button>
        </a>
        <img
          src={BRAND.thankYouCard}
          alt="Thank you from Regal Jewels"
          className="w-full h-auto mt-10 border border-[#eee6d6]"
          loading="lazy"
          data-testid="booking-thankyou-card"
        />
        <RegalClosing context="default" className="mt-10" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12">
      <Seo
        title="Book a Private Jewelry Consultation in Chicago"
        description="Reserve a one-on-one appointment at Regal Jewels on Devon Avenue, Chicago. Try on 22K gold and diamond jewelry, discuss custom orders, and get today's gold rates."
        path="/book"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Book a Meeting", path: "/book" },
        ])}
      />
      <div className="text-center mb-12">
        <p className="section-eyebrow">Private Consultation</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#171310]">Book a Meeting</h1>
        <p className="text-[#5c5240] mt-4 max-w-xl mx-auto">
          Reserve time with our jewelers at the Devon Avenue showroom — try on pieces, discuss custom orders, or get today's gold rates.
        </p>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-10">
        <div className="bg-white border border-[#eee6d6] p-6 sm:p-10">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Full Name *</Label>
              <Input data-testid="booking-name-input" value={form.name} onChange={set("name")} className="rounded-none border-[#d8cdb5] h-11" placeholder="Your name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Phone *</Label>
              <Input data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} className="rounded-none border-[#d8cdb5] h-11" placeholder="(773) 000-0000" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="rounded-none border-[#d8cdb5] h-11" placeholder="you@email.com" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">What are you shopping for?</Label>
              <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                <SelectTrigger className="rounded-none border-[#d8cdb5] h-11 bg-white">
                  <SelectValue placeholder="Select interest" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
                  {productInterest && <SelectItem value="Specific piece">Specific piece: {productInterest.slice(0, 40)}...</SelectItem>}
                  {INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-8 mt-8">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#7a7160] mb-3 block">Pick a Date *</Label>
              <div className="border border-[#e8e0d0] inline-block bg-white">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#7a7160] mb-3 block">Pick a Time *</Label>
              <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {TIME_SLOTS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, time: t })}
                    className={`text-xs py-2.5 border transition-colors duration-200 ${
                      form.time === t
                        ? "bg-[#171310] text-[#e9d9ac] border-[#171310]"
                        : "bg-white text-[#3a352c] border-[#d8cdb5] hover:border-[#a8842c]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1.5 mt-8">
            <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={3} className="rounded-none border-[#d8cdb5]" placeholder="Anything we should prepare for your visit?" />
          </div>

          <Button data-testid="booking-submit-btn" type="button" onClick={submit} className="w-full mt-8 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.15em] uppercase text-xs h-12 transition-colors duration-300">
            <CalendarDays size={15} className="mr-2" /> Request Appointment
          </Button>
        </div>

        {/* Side info */}
        <aside className="space-y-6">
          <img src={BRAND.collage} alt="Regal Jewels gold and diamond jewelry collections" className="w-full h-auto border border-[#eee6d6]" />
          <div className="bg-[#faf7f0] border border-[#ece5d5] p-8 text-[#5c5240]">
            <h3 className="font-heading text-2xl text-[#1a1713] mb-5">Visit Information</h3>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3"><MapPin size={17} className="text-[#a8842c] shrink-0 mt-0.5" /> {STORE.address}</li>
              <li className="flex gap-3"><Clock size={17} className="text-[#a8842c] shrink-0 mt-0.5" />
                <span>{STORE.hours.map((h) => <span key={h.d}>{h.d}: {h.h}<br /></span>)}</span>
              </li>
            </ul>
            <a href={waLink("Hello Regal Jewels! I'd like to book an appointment.")} target="_blank" rel="noopener noreferrer">
              <Button className="w-full mt-7 rounded-none bg-[#25D366] hover:bg-[#1fb457] text-white tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300">
                <MessageCircle size={15} className="mr-2" /> Book via WhatsApp
              </Button>
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}
